import type { FeaturedVideo, HomeSection } from '@/shared/types';
import { getJson, resolveApiUrl } from '@/shared/api/apiClient';

// GET /api/v1/home/featured — the curated Home-page list (valoi_engine
// app/routers/home_api.py). Same list for every signed-in user; ordering
// (curator position, then newest first) is decided server-side, so the client
// renders it as-is.
const FEATURED_PATH = '/api/v1/home/featured';

// GET /api/v1/home/sections — which rails exist, their labels, their order.
//
// A second request rather than a field on the feed, because the catalogue is
// not a property of any video: a section exists whether or not anything is in
// it, and the admin curation page needs the list without fetching the feed at
// all. Static server-side today; the signature is what survives a recommender.
const SECTIONS_PATH = '/api/v1/home/sections';

type FeaturedApiItem = {
  id: string;
  job_id: string;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url?: string | null;
  vtt_url?: string | null;
  tags?: string[] | null;
  category?: string | null;
  position?: number;
  published_at: string;
  job_status?: string | null;
  /*
   * The finished video's real runtime, ffprobed off the assembled file at upload
   * time. Explicitly NOT the engine's `Job.duration`, which is the requested
   * seconds *per scene* (5 or 10) and would badge a six-scene video as 0:10 —
   * the wrong-looking badge that sent us looking in the first place.
   *
   * This is the surface that needed the column most: a browse card has no loaded
   * <video> element to ask, so until it existed there was nothing honest to show
   * and BrowseCard rendered no badge at all. Still null for anything curated
   * from a job that finished before the column landed, which renders no badge.
   */
  runtime_seconds?: number | null;
};

function toFeaturedVideo(item: FeaturedApiItem): FeaturedVideo | null {
  // The public endpoint only returns playable entries, but a missing/unmintable
  // URL would render a dead hero player — drop such an item rather than crash.
  const videoUrl = resolveApiUrl(item.video_url);
  if (!videoUrl) return null;
  return {
    id: item.id,
    title: item.title,
    description: item.description,
    videoUrl,
    thumbnailUrl: item.thumbnail_url ? resolveApiUrl(item.thumbnail_url) : undefined,
    // The feed has always sent vtt_url; dropping it here is why a saved browse
    // video had no captions. These are CloudFront delivery URLs (no expiry),
    // so persisting them in a saved record is safe.
    vttUrl: item.vtt_url ? resolveApiUrl(item.vtt_url) : undefined,
    durationSeconds: item.runtime_seconds ?? undefined,
    tags: item.tags && item.tags.length > 0 ? item.tags : undefined,
    category: item.category ?? undefined,
    publishedAt: item.published_at,
  };
}

export async function fetchFeaturedVideos(): Promise<FeaturedVideo[]> {
  const items = await getJson<FeaturedApiItem[]>(FEATURED_PATH);
  return items.map(toFeaturedVideo).filter((item): item is FeaturedVideo => item !== null);
}

type HomeSectionApiItem = {
  slug: string;
  label: string;
  position: number;
};

/**
 * The Home rail catalogue, in the order it should render.
 *
 * Sorted here rather than trusted: `position` is what the engine says the order
 * is, and array order is only how it happened to arrive. Cheap insurance against
 * a proxy, a cache or a future paginated version reordering it silently.
 */
export async function fetchHomeSections(): Promise<HomeSection[]> {
  const items = await getJson<HomeSectionApiItem[]>(SECTIONS_PATH);
  return [...items].sort((a, b) => a.position - b.position);
}
